import { buttonClasses, IconButton as MuiIconButton, IconButtonProps as MuiIconButtonProps, styled } from "@mui/material"
import { ComponentType } from "react"
import { Button } from "./button"

export type IconButtonProps = Omit<MuiIconButtonProps, "size" | "color"> & {
    variant?: "contained"
    hoverEffect?: boolean
    noPadding?: boolean
    size?: "auto" | MuiIconButtonProps["size"]
    color?: "highlight" | MuiIconButtonProps["color"]
}

export function IconButtonComponent(props: MuiIconButtonProps & { variant?: "contained" }) {
    if (props.variant === "contained") {
        const { color, size, ...restProps } = props
        const sizeProp = size !== "extrasmall" ? size : undefined
        const colorProp = color !== "default" ? color : undefined
        return (
            <Button {...restProps} size={sizeProp} color={colorProp}>
                {props.children}
            </Button>
        )
    }
    return <MuiIconButton {...props}>{props.children}</MuiIconButton>
}

export const IconButton = styled(IconButtonComponent, {
    shouldForwardProp: (prop: string) => !["hoverEffect", "noPadding"].includes(prop),
})<IconButtonProps>(({ color, theme, size = "medium", hoverEffect, noPadding, variant }) => {
    const themeColor = color && (color === "inherit" ? undefined : theme.palette?.[color === "default" ? "primary" : color])

    return {
        color: themeColor?.main,
        verticalAlign: "top",

        ...(!variant && {
            "&.MuiButtonBase-root:hover": {
                backgroundColor: "inherit",
                ...(themeColor && {
                    color: themeColor.light,
                }),
            },
        }),

        ...(size === "extrasmall" && {
            padding: 1,
            svg: {
                fontSize: 12,
            },
        }),

        ...(size === "small" && {
            padding: 2,
            svg: {
                fontSize: 16,
            },
        }),

        ...(size === "medium" && {
            padding: 4,
            svg: {
                fontSize: 20,
            },
        }),

        ...(size === "large" && {
            padding: 6,
            svg: {
                fontSize: 24,
            },
        }),

        ...(size === "extralarge" && {
            padding: 8,
            svg: {
                fontSize: 32,
            },
        }),

        ...(noPadding && {
            padding: 0,
        }),

        ...(hoverEffect && {
            "&:hover": {
                color: theme.palette.primary.main,
            },
        }),

        ...(variant === "contained" && {
            [`&.${buttonClasses.contained}`]: {
                border: "none",
            },
            [`&.${buttonClasses.sizeSmall}`]: {
                padding: 2,
                svg: {
                    fontSize: 16,
                },
            },
            [`&.${buttonClasses.sizeMedium}`]: {
                padding: 3,
                svg: {
                    fontSize: 20,
                },
            },
            [`&.${buttonClasses.sizeLarge}`]: {
                padding: 4,
                svg: {
                    fontSize: 24,
                },
            },
            [`&.${buttonClasses.sizeExtraLarge}`]: {
                padding: 9,
                svg: {
                    fontSize: 30,
                },
            },
            svg: {
                margin: 0,
            },
        }),
    }
}) as ComponentType<IconButtonProps>
